// Receipt type definitions for the Receipts (printing) module.
// Each type renders a fixed title and an ordered list of fields. The form on the
// left renders an input per field; the preview/print on the right renders the
// same fields as a bordered table that mirrors the client's Excel layout.
//
// Field shape:
//   key       - unique id within the type (used as form state key)
//   labelKey  - i18n key for the field label (see LocaleContext)
//   type      - input/render kind:
//                 'customer' -> name combobox (pick existing customer OR free text)
//                 'text'     -> plain text
//                 'currency' -> number, rendered with a currency prefix
//                 'weight'   -> number, rendered with a 'g' suffix
//                 'percent'  -> number, rendered with a '%' suffix
//                 'number'   -> plain number
//                 'date'     -> free text date (matches Excel: "17 de junho 2026", "TODO DIA 27")
//   prefix    - rendered before the value (e.g. '$', 'R$')
//   suffix    - rendered after the value (e.g. 'g', '%')
//   required  - blocks print if empty
//   optional  - hint only; never blocks
//
// Store fixed strings on the receipt header/footer live in ReceiptPreview.

export const STORE_ADDRESS = 'ANAMOESTRAAT #15';

// Selectable currencies for receipts and the symbol shown before currency values.
export const RECEIPT_CURRENCIES = ['USD', 'EUR', 'SRD'];
export const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  SRD: 'SRD',
};

// Shared "amount" block: one optional input per currency (USD/EUR/SRD), each
// locked to its own symbol. Only the currencies the user fills in are rendered
// on the printed receipt. Used by payment / received / custody-release / loan.
const MULTI_CURRENCY_VALOR = [
  { key: 'valor_usd', labelKey: 'rcpt_f_valor', type: 'fixedCurrency', fixedCurrency: 'USD', optional: true },
  { key: 'valor_eur', labelKey: 'rcpt_f_valor', type: 'fixedCurrency', fixedCurrency: 'EUR', optional: true },
  { key: 'valor_srd', labelKey: 'rcpt_f_valor', type: 'fixedCurrency', fixedCurrency: 'SRD', optional: true },
];

export const RECEIPT_TYPES = [
  {
    id: 'custodia',
    titleKey: 'rcpt_type_custodia',
    fields: [
      { key: 'cliente', labelKey: 'rcpt_f_cliente', type: 'customer', required: true },
      { key: 'saldo_atual', labelKey: 'rcpt_f_saldo_atual', type: 'currency', prefix: '$' },
      { key: 'ultima_retirada', labelKey: 'rcpt_f_ultima_retirada', type: 'currency', prefix: '$' },
      { key: 'ultimo_deposito', labelKey: 'rcpt_f_ultimo_deposito', type: 'currency', prefix: '$' },
    ],
  },
  {
    id: 'guarda_ouro',
    titleKey: 'rcpt_type_guarda_ouro',
    fields: [
      { key: 'cliente', labelKey: 'rcpt_f_cliente', type: 'customer', required: true },
      { key: 'peso', labelKey: 'rcpt_f_peso', type: 'weight', suffix: 'g' },
      { key: 'teor', labelKey: 'rcpt_f_teor', type: 'percent', suffix: '%' },
      { key: 'data', labelKey: 'rcpt_f_data', type: 'date' },
    ],
  },
  {
    id: 'liberacao_custodia',
    titleKey: 'rcpt_type_liberacao_custodia',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      ...MULTI_CURRENCY_VALOR,
    ],
  },
  {
    id: 'pagamento',
    titleKey: 'rcpt_type_pagamento',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      ...MULTI_CURRENCY_VALOR,
    ],
  },
  {
    id: 'pix',
    titleKey: 'rcpt_type_pix',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      { key: 'banco', labelKey: 'rcpt_f_banco', type: 'text' },
      { key: 'valor', labelKey: 'rcpt_f_valor', type: 'currency', prefix: 'R$' },
    ],
  },
  {
    id: 'recebido',
    titleKey: 'rcpt_type_recebido',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      ...MULTI_CURRENCY_VALOR,
    ],
  },
  {
    id: 'encomenda',
    titleKey: 'rcpt_type_encomenda',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      { key: 'valor', labelKey: 'rcpt_f_valor', type: 'currency', prefix: '$' },
      { key: 'peso', labelKey: 'rcpt_f_peso', type: 'weight', suffix: 'g' },
    ],
  },
  {
    id: 'emprestimo',
    titleKey: 'rcpt_type_emprestimo',
    fields: [
      { key: 'de', labelKey: 'rcpt_f_de', type: 'customer', required: true },
      { key: 'para', labelKey: 'rcpt_f_para', type: 'customer', required: true },
      ...MULTI_CURRENCY_VALOR,
    ],
  },
  {
    id: 'penhor',
    titleKey: 'rcpt_type_penhor',
    fields: [
      { key: 'cliente', labelKey: 'rcpt_f_cliente', type: 'customer', required: true },
      { key: 'pecas', labelKey: 'rcpt_f_pecas', type: 'number' },
      { key: 'peso', labelKey: 'rcpt_f_peso', type: 'weight', suffix: 'g' },
      { key: 'v_emprestado', labelKey: 'rcpt_f_v_emprestado', type: 'currency', prefix: '$' },
      { key: 'juros_pct', labelKey: 'rcpt_f_juros_pct', type: 'percent', suffix: '% ao mês' },
      { key: 'juros_valor', labelKey: 'rcpt_f_juros_valor', type: 'currency', prefix: '$' },
      { key: 'vencimento', labelKey: 'rcpt_f_vencimento', type: 'date' },
    ],
  },
  {
    id: 'compra_ouro',
    titleKey: 'rcpt_type_compra_ouro',
    // Custom receipt layout (see ReceiptPreview): "peso X preco / TOTAL / PAGO".
    layout: 'compra_ouro',
    fields: [
      { key: 'cliente', labelKey: 'rcpt_f_cliente', type: 'customer' },
      { key: 'peso', labelKey: 'rcpt_f_peso', type: 'weight', suffix: 'g' },
      { key: 'preco', labelKey: 'rcpt_f_preco', type: 'currency', prefix: '$' },
      // Total auto-fills from peso x preco (autoFill), but stays editable.
      { key: 'total', labelKey: 'rcpt_f_total', type: 'currency', prefix: '$', suggest: { op: 'multiply', from: ['peso', 'preco'], autoFill: true } },
      // Paid amounts: one per currency, each fixed to its own symbol.
      { key: 'pago_usd', labelKey: 'rcpt_f_pago', type: 'fixedCurrency', fixedCurrency: 'USD' },
      { key: 'pago_eur', labelKey: 'rcpt_f_pago', type: 'fixedCurrency', fixedCurrency: 'EUR' },
      { key: 'pago_srd', labelKey: 'rcpt_f_pago', type: 'fixedCurrency', fixedCurrency: 'SRD' },
    ],
  },
  {
    id: 'juros_penhor',
    titleKey: 'rcpt_type_juros_penhor',
    fields: [
      { key: 'cliente', labelKey: 'rcpt_f_cliente', type: 'customer', required: true },
      { key: 'data', labelKey: 'rcpt_f_data', type: 'date' },
      { key: 'juros', labelKey: 'rcpt_f_juros', type: 'currency', prefix: '$' },
    ],
  },
];

export function getReceiptType(id) {
  return RECEIPT_TYPES.find((t) => t.id === id) || null;
}

// True if the type has at least one currency field (so the currency selector
// is only shown when relevant).
export function typeHasCurrency(type) {
  return !!type && type.fields.some((f) => f.type === 'currency');
}

/**
 * Compute a field's suggested value from other field values, per its `suggest`
 * meta. Returns a number, or null if inputs are missing/invalid.
 * @param {object} field   field def (may have .suggest)
 * @param {object} values  current form values
 * @returns {number|null}
 */
export function computeSuggestion(field, values) {
  if (!field || !field.suggest) return null;
  const { op, from } = field.suggest;
  const nums = from.map((k) => parseFloat(values[k]));
  if (nums.some((n) => !Number.isFinite(n))) return null;

  if (op === 'multiply') return nums.reduce((a, b) => a * b, 1);
  if (op === 'add') return nums.reduce((a, b) => a + b, 0);
  return null;
}
